import React, { useCallback, useEffect, useRef, useState } from 'react';

const AnimatedOrgChartConnector = ({ selectedEmployeeId, reportsSectionRef, employees }) => {
  const [path, setPath] = useState('');
  const [pathLength, setPathLength] = useState(0);
  const [pathVisible, setPathVisible] = useState(false);
  const [dashOffset, setDashOffset] = useState(0);
  const containerRef = useRef(null);
  const previousSelectedId = useRef(null);
  const pathRef = useRef(null);

  // Reset visibility when employee changes
  useEffect(() => {
    if (previousSelectedId.current !== selectedEmployeeId) {
      setPathVisible(false);
    }
  }, [selectedEmployeeId]);

  const checkIfVerticallyStacked = useCallback(() => {
    if (!selectedEmployeeId) return false;

    const selectedElement = document.getElementById(`employee-${selectedEmployeeId}`);
    if (!selectedElement) return false;

    // Find all employees at the same level as the selected employee
    const selectedEmployeeData = employees.find((emp) => emp.id === selectedEmployeeId);
    if (!selectedEmployeeData) return false;

    const sameLevel = employees.filter(
      (emp) => emp.level === selectedEmployeeData.level && emp.id !== selectedEmployeeId
    );

    // If there are no other employees at this level, no stacking is possible
    if (sameLevel.length === 0) return false;

    // Check if any employee at the same level has a significantly different Y position
    let isStacked = false;
    for (const emp of sameLevel) {
      const empElement = document.getElementById(`employee-${emp.id}`);
      if (empElement) {
        const empRect = empElement.getBoundingClientRect();
        const selectedRect = selectedElement.getBoundingClientRect();

        // If the vertical positions differ by more than a threshold, they're on different rows (stacked)
        const verticalThreshold = selectedRect.height * 0.5;
        if (Math.abs(empRect.top - selectedRect.top) > verticalThreshold) {
          isStacked = true;
          break;
        }
      }
    }

    return isStacked;
  }, [selectedEmployeeId, employees]);

  const calculatePath = useCallback(() => {
    if (!selectedEmployeeId || !reportsSectionRef.current || !containerRef.current) {
      return null;
    }

    const selectedElement = document.getElementById(`employee-${selectedEmployeeId}`);
    if (!selectedElement) {
      return null;
    }

    const stacked = checkIfVerticallyStacked();

    const employeeRect = selectedElement.getBoundingClientRect();
    const reportsRect = reportsSectionRef.current.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();

    let pathData;

    if (stacked) {
      // For stacked layout, start from the LEFT side of the employee rectangle
      const startX = employeeRect.left - containerRect.left; // Left edge of employee element
      const startY = employeeRect.top + employeeRect.height / 2 - containerRect.top; // Middle of the left side

      // End coordinates (reports section)
      const endX = reportsRect.left; // Left edge of reports section
      const endY = reportsRect.top - containerRect.top;

      // Path: Start from left side, go left, then down to reports section
      pathData = `M ${startX},${startY} 
                    L ${endX},${startY} 
                    L ${endX},${endY}`;
    } else {
      // For non-stacked layout, use the bottom center approach
      const startX = employeeRect.left + employeeRect.width / 2 - containerRect.left;
      const startY = employeeRect.bottom - containerRect.top;
      const endY = reportsRect.top - containerRect.top;

      // Path: Straight down from bottom center
      pathData = `M ${startX},${startY} L ${startX},${endY}`;
    }

    return pathData;
  }, [selectedEmployeeId, reportsSectionRef, checkIfVerticallyStacked]);

  const getPathLength = useCallback((pathData) => {
    if (!pathData) return 0;

    try {
      const tempPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      tempPath.setAttribute('d', pathData);
      return tempPath.getTotalLength();
    } catch (e) {
      console.error('Error calculating path length:', e);
      return 1000; // Fallback value
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const newPath = calculatePath();
      if (newPath) {
        setPath(newPath);
        const newLength = getPathLength(newPath);
        setPathLength(newLength);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [calculatePath, getPathLength]);

  useEffect(() => {
    // Clear any previous path when employee changes
    if (previousSelectedId.current !== selectedEmployeeId) {
      setPathVisible(false);
      setPath('');

      // Wait a little, then calculate the new path
      const timer1 = setTimeout(() => {
        const newPath = calculatePath();
        if (newPath) {
          setPath(newPath);
          const newLength = getPathLength(newPath);
          setPathLength(newLength);
          setDashOffset(newLength);

          // Wait a bit more to ensure DOM has updated
          const timer2 = setTimeout(() => {
            setPathVisible(true);

            if (pathRef.current) {
              // Force layout recalculation
              pathRef.current.getBoundingClientRect();
            }

            // Start animation
            const timer3 = setTimeout(() => {
              setDashOffset(0);
            }, 20);

            return () => clearTimeout(timer3);
          }, 30);

          return () => clearTimeout(timer2);
        }
      }, 100);

      previousSelectedId.current = selectedEmployeeId;
      return () => clearTimeout(timer1);
    }
  }, [selectedEmployeeId, calculatePath, getPathLength]);

  return (
    <div ref={containerRef} className="pointer-events-none absolute left-0 top-0 h-full w-full">
      <svg className="absolute left-0 top-0 h-full w-full" style={{ zIndex: 1 }}>
        {path && pathVisible && (
          <path
            ref={pathRef}
            d={path}
            stroke="#F32A52"
            strokeWidth="2"
            fill="none"
            strokeDasharray={pathLength}
            strokeDashoffset={dashOffset}
            style={{
              transition: 'stroke-dashoffset 0.4s ease-out',
            }}
          />
        )}
      </svg>
    </div>
  );
};

export default AnimatedOrgChartConnector;
